"use client";

import { useRef, useEffect, useState, useCallback } from "react";
interface ParticleImageProps {
  imageUrl: string;
  imageWidth?: number;
  imageHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  mobileMaxHeight?: number;
  particleGap?: number;
  gravity?: number;
  noise?: number;
  mouseForce?: number;
  particleColor?: string;
  scatterColor?: string;
  useImageColor?: boolean;
  particleSize?: number | (() => number);
  targetParticleCount?: number;
  disableInteraction?: boolean;
  mouseEffectRadius?: number;
  particleLife?: number | (() => number);
  backgroundColor?: string | null;
  containerHeight?: string;
  className?: string;
}
interface Particle {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  size: number;
  color: string;
  originalColor: string;
  scatterEffectColor: string;
  life: number;
  vx: number;
  vy: number;
}
const defaultProps: Partial<ParticleImageProps> = {
  particleGap: 2,
  gravity: 0.08,
  noise: 1,
  mouseForce: 60,
  particleColor: "#FFFFFF",
  scatterColor: "#00DCFF",
  useImageColor: false,
  particleSize: 1,
  disableInteraction: false,
  mouseEffectRadius: 240,
  particleLife: 100,
  backgroundColor: "#000000"
};
const MAX_PARTICLE_CREATION_ATTEMPTS = 100;
const DENSITY_DIVISOR = 3;
export default function ParticleImage(props: ParticleImageProps) {
  const {
    imageUrl,
    imageWidth: customImageWidth,
    imageHeight: customImageHeight,
    maxWidth: userMaxWidth,
    maxHeight: userMaxHeight,
    mobileMaxHeight,
    particleGap = defaultProps.particleGap!,
    gravity = defaultProps.gravity!,
    noise = defaultProps.noise!,
    mouseForce = defaultProps.mouseForce!,
    particleColor: monoParticleColor = defaultProps.particleColor!,
    scatterColor = defaultProps.scatterColor!,
    useImageColor = defaultProps.useImageColor!,
    particleSize: particleSizeOpt = defaultProps.particleSize!,
    targetParticleCount: userTargetParticleCount,
    disableInteraction = defaultProps.disableInteraction!,
    mouseEffectRadius = defaultProps.mouseEffectRadius!,
    particleLife: particleLifeOpt = defaultProps.particleLife!,
    backgroundColor = defaultProps.backgroundColor!,
    containerHeight,
    className
  } = {
    ...defaultProps,
    ...props
  };
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mousePositionRef = useRef({
    x: -10000,
    y: -10000
  });
  const isTouchingRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const particlesRef = useRef<Particle[]>([]);
  const imageDataRef = useRef<ImageData | null>(null);
  const imageDisplayConfigRef = useRef({
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });
  const animationFrameIdRef = useRef<number>(0);

  // Use mounted state to prevent hydration issues
  useEffect(() => {
    setMounted(true);
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  const getParticleSize = useCallback(() => {
    return typeof particleSizeOpt === "function" ? particleSizeOpt() : particleSizeOpt;
  }, [particleSizeOpt]);
  const getParticleLife = useCallback(() => {
    return typeof particleLifeOpt === "function" ? particleLifeOpt() : particleLifeOpt;
  }, [particleLifeOpt]);
  const createParticle = useCallback((): Particle | null => {
    const imgData = imageDataRef.current;
    const imgConfig = imageDisplayConfigRef.current;
    if (!imgData || !imgConfig.width || !imgConfig.height) return null;
    for (let attempt = 0; attempt < MAX_PARTICLE_CREATION_ATTEMPTS; attempt++) {
      const tryX = Math.floor(Math.random() * imgConfig.width);
      const tryY = Math.floor(Math.random() * imgConfig.height);
      const pixelIndex = (tryY * imgConfig.width + tryX) * 4;
      const alpha = imgData.data[pixelIndex + 3];
      if (alpha > 128) {
        const r = imgData.data[pixelIndex];
        const g = imgData.data[pixelIndex + 1];
        const b = imgData.data[pixelIndex + 2];
        const originalColor = useImageColor ? `rgb(${r},${g},${b})` : monoParticleColor;
        return {
          x: imgConfig.x + tryX + (Math.random() - 0.5) * particleGap,
          y: imgConfig.y + tryY + (Math.random() - 0.5) * particleGap,
          baseX: imgConfig.x + tryX,
          baseY: imgConfig.y + tryY,
          size: getParticleSize(),
          originalColor: originalColor,
          scatterEffectColor: scatterColor,
          color: originalColor,
          life: getParticleLife(),
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5
        };
      }
    }
    return null;
  }, [useImageColor, monoParticleColor, scatterColor, getParticleSize, getParticleLife, particleGap]);
  const populateParticles = useCallback(() => {
    const imgConfig = imageDisplayConfigRef.current;
    if (!imgConfig.width || !imgConfig.height) {
      particlesRef.current = [];
      return;
    }
    const numParticles = userTargetParticleCount ?? Math.max(100, Math.floor(imgConfig.width * imgConfig.height / (particleGap * particleGap * DENSITY_DIVISOR)));
    const newParticles: Particle[] = [];
    for (let i = 0; i < numParticles; i++) {
      const p = createParticle();
      if (p) newParticles.push(p);
    }
    particlesRef.current = newParticles;
  }, [userTargetParticleCount, particleGap, createParticle]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", {
      willReadFrequently: false
    });
    if (!ctx) return;
    const updateCanvasSize = () => {
      // Get the actual container dimensions instead of window dimensions
      const container = canvas.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        canvas.width = containerRect.width;
        canvas.height = containerRect.height;
      } else {
        // Fallback to window dimensions
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };
    const image = new Image();
    image.crossOrigin = "anonymous";
    const initializeScene = () => {
      updateCanvasSize();
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const imgNaturalWidth = image.naturalWidth;
      const imgNaturalHeight = image.naturalHeight;
      if (imgNaturalWidth === 0 || imgNaturalHeight === 0) return;
      let targetWidth = imgNaturalWidth;
      let targetHeight = imgNaturalHeight;
      if (customImageWidth && !customImageHeight) {
        targetWidth = customImageWidth;
        targetHeight = imgNaturalHeight * (customImageWidth / imgNaturalWidth);
      } else if (!customImageWidth && customImageHeight) {
        targetHeight = customImageHeight;
        targetWidth = imgNaturalWidth * (customImageHeight / imgNaturalHeight);
      } else if (customImageWidth && customImageHeight) {
        targetWidth = customImageWidth;
        targetHeight = customImageHeight;
      }
      const effectiveMaxWidth = userMaxWidth ?? (isMobile ? canvasWidth * 0.8 : canvasWidth * 0.6);

      // Use mobileMaxHeight if provided and on mobile, otherwise use userMaxHeight or default
      let effectiveMaxHeight;
      if (isMobile && mobileMaxHeight) {
        effectiveMaxHeight = mobileMaxHeight;
      } else if (userMaxHeight) {
        effectiveMaxHeight = userMaxHeight;
      } else {
        effectiveMaxHeight = isMobile ? canvasHeight * 0.8 : canvasHeight * 0.8;
      }

      // Calculate scale to fit within bounds while maintaining aspect ratio
      const scaleToFitWidth = effectiveMaxWidth / targetWidth;
      const scaleToFitHeight = effectiveMaxHeight / targetHeight;
      const scale = Math.min(scaleToFitWidth, scaleToFitHeight, 1); // Don't scale up

      const displayWidth = Math.floor(targetWidth * scale);
      const displayHeight = Math.floor(targetHeight * scale);
      imageDisplayConfigRef.current = {
        x: (canvasWidth - displayWidth) / 2,
        y: (canvasHeight - displayHeight) / 2,
        width: displayWidth,
        height: displayHeight
      };
      const offscreenCanvas = document.createElement("canvas");
      offscreenCanvas.width = displayWidth;
      offscreenCanvas.height = displayHeight;
      const offscreenCtx = offscreenCanvas.getContext("2d", {
        willReadFrequently: true
      });
      if (!offscreenCtx) return;
      offscreenCtx.drawImage(image, 0, 0, displayWidth, displayHeight);
      imageDataRef.current = offscreenCtx.getImageData(0, 0, displayWidth, displayHeight);
      populateParticles();
    };
    image.onload = () => {
      initializeScene();
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
      animate();
    };
    image.onerror = () => console.error("ParticleImage: Failed to load image -", imageUrl);
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = 0;
    }
    particlesRef.current = [];
    imageDataRef.current = null;
    image.src = imageUrl;
    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      const {
        x: mouseX,
        y: mouseY
      } = mousePositionRef.current;
      const currentParticles = particlesRef.current;
      for (let i = 0; i < currentParticles.length; i++) {
        const p = currentParticles[i];
        let finalColor = p.originalColor;
        let interactionForceX = 0;
        let interactionForceY = 0;
        if (!disableInteraction && (isTouchingRef.current || mouseX > -1000)) {
          const dx = mouseX - p.x;
          const dy = mouseY - p.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < mouseEffectRadius) {
            const forceFactor = (mouseEffectRadius - distance) / mouseEffectRadius;
            const angle = Math.atan2(dy, dx);
            interactionForceX = Math.cos(angle) * forceFactor * mouseForce;
            interactionForceY = Math.sin(angle) * forceFactor * mouseForce;
            // Only change color if scatterColor is not transparent
            if (scatterColor !== "transparent") {
              finalColor = p.scatterEffectColor;
            }
          }
        }
        p.color = finalColor;
        const toBaseX = p.baseX - p.x;
        const toBaseY = p.baseY - p.y;
        p.x += (toBaseX - interactionForceX) * gravity;
        p.y += (toBaseY - interactionForceY) * gravity;
        if (noise > 0) {
          p.vx += (Math.random() - 0.5) * 0.1 * noise;
          p.vy += (Math.random() - 0.5) * 0.1 * noise;
          p.vx *= 0.95;
          p.vy *= 0.95;
          p.x += p.vx;
          p.y += p.vy;
        }
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
        p.life--;
        if (p.life <= 0) {
          const newParticle = createParticle();
          if (newParticle) {
            currentParticles[i] = newParticle;
          } else {
            currentParticles.splice(i, 1);
            i--;
          }
        }
      }
      const targetCount = userTargetParticleCount ?? Math.max(100, Math.floor(imageDisplayConfigRef.current.width * imageDisplayConfigRef.current.height / (particleGap * particleGap * DENSITY_DIVISOR)));
      const MAX_TOTAL_PARTICLES = 50000;
      while (currentParticles.length < targetCount && currentParticles.length < MAX_TOTAL_PARTICLES) {
        const newP = createParticle();
        if (newP) currentParticles.push(newP);else break;
      }
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };
    const handleResize = () => {
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = 0;

      // Always update canvas size first
      updateCanvasSize();
      if (image.complete && image.naturalWidth > 0) {
        initializeScene();
        animate();
      }
    };
    const handlePointerMove = (clientX: number, clientY: number) => {
      if (disableInteraction) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      mousePositionRef.current = {
        x,
        y
      };
    };
    const handleMouseMove = (e: MouseEvent) => {
      handlePointerMove(e.clientX, e.clientY);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handlePointerMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const handleTouchStart = (e: TouchEvent) => {
      if (disableInteraction) return;
      isTouchingRef.current = true;
      if (e.touches.length > 0) {
        handlePointerMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const handleTouchEnd = () => {
      if (disableInteraction) return;
      isTouchingRef.current = false;
      mousePositionRef.current = {
        x: -10000,
        y: -10000
      };
    };
    const handleMouseLeave = () => {
      if (disableInteraction) return;
      if (!("ontouchstart" in window) && !isTouchingRef.current) {
        mousePositionRef.current = {
          x: -10000,
          y: -10000
        };
      }
    };

    // Use ResizeObserver to watch container size changes
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }
    window.addEventListener("resize", handleResize);
    if (!disableInteraction) {
      canvas.addEventListener("mousemove", handleMouseMove);
      canvas.addEventListener("touchmove", handleTouchMove, {
        passive: true
      });
      canvas.addEventListener("touchstart", handleTouchStart, {
        passive: true
      });
      canvas.addEventListener("touchend", handleTouchEnd);
      canvas.addEventListener("mouseleave", handleMouseLeave);
    }
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
      if (!disableInteraction) {
        canvas.removeEventListener("mousemove", handleMouseMove);
        canvas.removeEventListener("touchmove", handleTouchMove);
        canvas.removeEventListener("touchstart", handleTouchStart);
        canvas.removeEventListener("touchend", handleTouchEnd);
        canvas.removeEventListener("mouseleave", handleMouseLeave);
      }
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = 0;
    };
  }, [imageUrl, customImageWidth, customImageHeight, userMaxWidth, userMaxHeight, mobileMaxHeight, particleGap, gravity, noise, mouseForce, monoParticleColor, scatterColor, useImageColor, particleSizeOpt, userTargetParticleCount, disableInteraction, mouseEffectRadius, particleLifeOpt, backgroundColor, isMobile, createParticle, populateParticles, getParticleSize, getParticleLife]);

  // Don't render until mounted to prevent hydration issues
  if (!mounted) {
    return <div className={`relative flex w-full flex-col items-center justify-center ${containerHeight || "h-32"} ${className || ""}`} style={{
      background: backgroundColor === null ? "transparent" : backgroundColor
    }} />;
  }
  return <div className={`relative flex w-full flex-col items-center justify-center ${containerHeight || "h-dvh"} ${className || ""}`} style={{
    background: backgroundColor === null ? "transparent" : backgroundColor
  }} data-sentry-component="ParticleImage" data-sentry-source-file="particle-image.tsx">
      <canvas ref={canvasRef} className="absolute left-0 top-0 h-full w-full" style={{
      touchAction: disableInteraction ? "auto" : "none"
    }} aria-label="Interactive particle image effect" />
    </div>;
}